
import { useEffect } from "react";
import { useJournal } from "@/hooks/use-journal";
import { JournalForm } from "@/components/journal/JournalForm";
import { JournalEntryList } from "@/components/journal/JournalEntryList";
import { useFeatureLimit } from "@/hooks/useFeatureLimit";
import { useSession } from "@supabase/auth-helpers-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { InfoIcon } from "lucide-react";

interface JournalTabProps {
  remainingFreeEntries?: number;
}

export function JournalTab({ remainingFreeEntries }: JournalTabProps) {
  const { journalEntries, handleJournalSubmit, handleSuggestVerse } = useJournal();
  const { incrementUsage, features } = useFeatureLimit();
  const session = useSession();

  // Ensure the component always renders at the top when loaded
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleFormSubmit = (data: {
    content: string;
    moodRating: string;
    selectedTags: string[];
  }) => {
    // If user is not logged in, check their usage limit
    if (!session) {
      if (!incrementUsage(features.JOURNAL_ENTRY.key)) {
        return false;
      }
    }
    
    // Submit the journal entry
    handleJournalSubmit({
      content: data.content,
      moodRating: data.moodRating as any,
      selectedTags: data.selectedTags
    });
    return true;
  };

  return (
    <div className="space-y-6 pb-16">
      {!session && remainingFreeEntries !== undefined && remainingFreeEntries === 1 && (
        <Alert className="bg-blue-900/30 border-blue-800 text-blue-100">
          <InfoIcon className="h-4 w-4" />
          <AlertDescription>
            This is your free weekly journal entry. Sign up for unlimited entries.
          </AlertDescription>
        </Alert>
      )}
      
      <JournalForm 
        onSubmit={handleFormSubmit}
        onSuggestVerse={handleSuggestVerse}
        disabled={remainingFreeEntries !== undefined && remainingFreeEntries <= 0}
      />
      
      <JournalEntryList entries={journalEntries} />
    </div>
  );
}
